import {
  type MediaCardType,
  mediaCardTypeToKeys,
  mediaStrings as MEDIA_STRINGS,
} from '@wandb/weave/common/types/media';
import memoize from 'memoize-one';

import {type RunHistoryKeyInfo} from '../../../types/run';
import {startFunctionPerformanceMeasure} from '../../../util/datadog/workspace/functionPerformance';
import {dominantKeyType} from '../../../util/runhelpers';
import {type KeyTypes} from '../common';

const MEDIA_STRINGS_SET = new Set<string>(MEDIA_STRINGS);

type MediaKeysInfo = {
  allMediaKeys: string[];
  defaultKeys: string[] | undefined;
  keyTypes: KeyTypes;
  numericKeys: string[];
};

function getMediaKeysImpl(keyInfo: RunHistoryKeyInfo): MediaKeysInfo {
  const keys = keyInfo.keys;
  // Test exact release cohorts in fixed key-count buckets after history-key
  // refreshes. Duration * 1000 / keyCount gives microseconds per history key;
  // pair it with the later keyTypes-paying autosync span because the stacked
  // change removes a duplicated cycle-level scan, not necessarily work local
  // to this function.
  const endMeasure = startFunctionPerformanceMeasure('getMediaKeysImpl');

  const keyTypes: KeyTypes = {};
  const allMediaKeys: string[] = [];
  const numericKeys: string[] = [];

  // Single pass over the history keys to classify each one, rather than
  // building the full type map and then re-scanning it (with lodash) once per
  // type. The key set can be very large (100k+), so the extra passes are costly.
  for (const key of Object.keys(keys)) {
    const type = dominantKeyType(keys[key].typeCounts);
    if (type === undefined) {
      continue;
    }
    keyTypes[key] = type;
    if (MEDIA_STRINGS_SET.has(type)) {
      allMediaKeys.push(key);
    } else if (type === 'number') {
      numericKeys.push(key);
    }
  }

  const defaultKeys = allMediaKeys.length > 0 ? [allMediaKeys[0]] : undefined;

  const result = {
    allMediaKeys,
    defaultKeys,
    keyTypes,
    numericKeys,
  };
  endMeasure({keyCount: keyInfo.sortedKeys.length});
  return result;
}

export const getMediaKeys = memoize(getMediaKeysImpl);

/**
 * First key of `mediaType`, so a panel added as Images doesn't open on whatever
 * key `defaultKeys` picks (e.g. a 3D object).
 */
export function getDefaultMediaKeysForType(
  {allMediaKeys, keyTypes}: Pick<MediaKeysInfo, 'allMediaKeys' | 'keyTypes'>,
  mediaType: MediaCardType
): string[] | undefined {
  const matchingTypes = new Set<string>(mediaCardTypeToKeys(mediaType));
  const key = allMediaKeys.find(k => {
    const type = keyTypes[k];
    return type != null && matchingTypes.has(type);
  });
  return key != null ? [key] : undefined;
}

export function getMediaKeysDisplayStringParts(mediaKeys: string[]): {
  type: 'element' | 'literal';
  value: string;
}[] {
  // casting to `any` avoids the ts(2339) error for ListFormat not existing on Intl
  // likely can be removed if we upgrade to a more recent target version
  const formatter = new (Intl as any).ListFormat('en', {
    style: 'long',
    type: 'conjunction',
  });
  return formatter.formatToParts(mediaKeys);
}
